import { useHistory, useRouteMatch } from "react-router-dom";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { StateData } from "types/Models/nexusTracker";
import { Button } from "components/DesignSystem/Button/Button";
import { RegisterStateDropdown } from "components/SalesTax/RegisterStateDropdown";
import { useLazyGetFilingDetailsByStateIdQuery } from "store/apis/filing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { getBillingCartUrl } from "util/getBillingCartUrl";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  APPROACHING,
  EXPOSED,
  MONITORING,
  REGISTERING,
  REMITTING,
} from "constants/nexusTrackerStatus";
import { FILING_INFO_MISSING } from "constants/salesTaxRegistrationStatusType";

export enum ActionButtonText {
  MONITORING = "View",
  EXPOSED = "Register",
  GO_TO_CART = "Go to cart",
  REGISTERING = "View registration",
  REMITTING = "View",
  FILING_INFO_MISSING = "Add filing details",
}

// The tracker's `state_registration_status` only ever collapses to
// REGISTERING/REMITTING, hiding a state that has finished registering but
// still needs its filing details (payment schedule, filing start date).
// Surface that state distinctly, same as the Registrations page does.
export const getDisplayStatus = (rowData: {
  registration_status?: string;
  state_registration_status?: string;
  status: string;
}): string => {
  if (rowData.registration_status === FILING_INFO_MISSING) {
    return FILING_INFO_MISSING;
  }
  return rowData.state_registration_status || rowData.status;
};

export const getActionButtonText = (
  status: string,
  isInCart?: boolean,
  hasLaunchedTask?: boolean
): ActionButtonText | "" => {
  switch (status) {
    case MONITORING:
      return ActionButtonText.MONITORING;
    case APPROACHING:
    case EXPOSED:
      return isInCart ? ActionButtonText.GO_TO_CART : ActionButtonText.EXPOSED;
    case REGISTERING:
      return hasLaunchedTask
        ? ActionButtonText.REMITTING
        : ActionButtonText.REGISTERING;
    case REMITTING:
      return ActionButtonText.REMITTING;
    case FILING_INFO_MISSING:
      return ActionButtonText.FILING_INFO_MISSING;
    default:
      return "";
  }
};

export const ActionCell = ({
  rowData,
  onOpenSlider,
  isExempted,
}: {
  rowData: StateData;
  onOpenSlider: (jurisdiction: string) => void;
  isExempted?: boolean;
}) => {
  const { data: countries } = useGetCountriesDataQuery();
  const { location, push } = useHistory();
  const { path } = useRouteMatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();

  const status = getDisplayStatus(rowData);
  const isInCart = rowData.cart_status === "IN_CART";
  const hasLaunchedTask = Boolean(rowData.launched_task_id);
  const actionText = getActionButtonText(status, isInCart, hasLaunchedTask);

  const [getFilingDetailsByStateId] = useLazyGetFilingDetailsByStateIdQuery();
  const entityId = useCurrentEntityId();

  const unitatedStates = countries?.find(
    (country) => country.name === "United States"
  )?.states;

  const stateId = unitatedStates?.find(
    (state) => state.name === rowData.jurisdiction
  )?.uuid;

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();

    if (actionText === ActionButtonText.GO_TO_CART) {
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
      return;
    }

    if (actionText === ActionButtonText.REMITTING && rowData.launched_task_id) {
      window.open(`/tax/filings/${rowData.launched_task_id}`, "_blank");
      return;
    }

    if (!stateId) {
      return;
    }

    if (actionText === ActionButtonText.REMITTING) {
      const currentParams = new URLSearchParams(location.search);
      try {
        const resp = await getFilingDetailsByStateId({
          stateId,
          entityId,
        }).unwrap();
        currentParams.set("tax_year", String(resp.filing_tax_year));
        currentParams.set("filing_id", String(resp.uuid));
        window.open(
          `${path}/state-filings?${currentParams.toString()}`,
          "_blank"
        );
      } catch (error) {
        onOpenSlider(rowData.jurisdiction);
      }
    }

    if (
      actionText === ActionButtonText.REGISTERING ||
      actionText === ActionButtonText.FILING_INFO_MISSING
    ) {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("type", "existing");
      currentParams.set("state_id", stateId);
      push(`${path}/registrations?${currentParams.toString()}`);
    }
  };

  const showRegisterDropdown =
    actionText === ActionButtonText.EXPOSED && stateId && !isExempted;

  if (showRegisterDropdown) {
    return (
      <div
        className="t-text-center t-flex t-flex-col t-items-center t-justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <RegisterStateDropdown
          entityId={entityId}
          stateId={stateId}
          stateName={rowData.jurisdiction}
        />
      </div>
    );
  }

  const isMonitoring = status === MONITORING;

  return (
    <div className="t-text-center t-flex t-flex-col t-items-center t-justify-center">
      {!isMonitoring && !isExempted ? (
        <Button
          size="small"
          onClick={handleClick}
          customType={
            actionText === ActionButtonText.GO_TO_CART ? "secondary" : "ghost"
          }
        >
          <span className="t-text-subtext-sm t-text-text-60">{actionText}</span>
        </Button>
      ) : (
        <span className="t-text-text-60 t-text-subtext-sm t-text-nowrap">
          {isExempted ? "View" : actionText}
        </span>
      )}
    </div>
  );
};
